import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Utils
import axios from '../../axios';
import login, { hadTransientRefreshError } from '../../utils/spotify/login';

// Services
import { authService } from '../../services/auth';

// Interfaces
import type { User } from '../../interfaces/user';
import { getFromLocalStorageWithExpiry } from '../../utils/localstorage';

const maskToken = (token?: string | null) => {
  if (!token) return 'null';
  if (token.length <= 8) return token;
  return `${token.slice(0, 4)}...${token.slice(-4)}`;
};

const initialState: { token?: string; playerLoaded: boolean; user?: User; requesting: boolean } = {
  user: undefined,
  requesting: true,
  playerLoaded: false,
  token: getFromLocalStorageWithExpiry('access_token') || undefined,
};

let loginInFlight: Promise<{ token?: string; loaded: boolean; redirect?: boolean }> | null = null;

export const loginToSpotify = createAsyncThunk<{ token?: string; loaded: boolean; redirect?: boolean }, boolean>(
  'auth/loginToSpotify',
  async (anonymous, api) => {
    if (loginInFlight) {
      console.info('[AuthTrace] loginToSpotify returning in-flight login');
      return loginInFlight;
    }
    api.dispatch(authActions.setRequesting({ requesting: true }));
    console.info('[AuthTrace] loginToSpotify start', { anonymous });
    loginInFlight = (async () => {
      let launchedRedirect = false;
      try {
        // getToken() now checks for callback code first, so it will process callback
        // even if a token exists (handles new account auth with old token present)
        let [requestedToken, requestUser] = await login.getToken();
        console.info('[AuthTrace] loginToSpotify getToken resolved', {
          hasToken: !!requestedToken,
          tokenMasked: maskToken(requestedToken ?? undefined),
          requestUser,
        });
        
        if (requestedToken) {
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + requestedToken;
          if (requestUser) {
            api.dispatch(fetchUser());
          }
          // Clear callback code from URL if it was present
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('code')) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          return { token: requestedToken, loaded: !!requestUser, redirect: false };
        }

        if (hadTransientRefreshError()) {
          console.info('[AuthTrace] loginToSpotify transient refresh error detected; skipping redirect');
          return { token: undefined, loaded: false, redirect: false };
        }

        // No token - start OAuth flow
        console.info('[AuthTrace] loginToSpotify no token, initiating OAuth', { anonymous });
        launchedRedirect = await login.logInWithSpotify(anonymous);
        return { token: undefined, loaded: false, redirect: launchedRedirect };
      } catch (error) {
        console.error('[loginToSpotify] Error in getToken:', error);
        // Re-throw so the thunk is marked as rejected and requesting is set to false
        throw error;
      } finally {
        // Keep the in-flight promise sticky if we launched a redirect, so we don't trigger another one
        if (!launchedRedirect) {
          loginInFlight = null;
        }
      }
    })();

    return loginInFlight;
  }
);

export const fetchUser = createAsyncThunk('auth/fetchUser', async () => {
  const response = await authService.fetchUser();
  return response.data;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setRequesting(state, action: PayloadAction<{ requesting: boolean }>) {
      state.requesting = action.payload.requesting;
    },
    setToken(state, action: PayloadAction<{ token?: string }>) {
      state.token = action.payload.token;
    },
    setPlayerLoaded(state, action: PayloadAction<{ playerLoaded: boolean }>) {
      state.playerLoaded = action.payload.playerLoaded;
    },
    removeUser(state) {
      console.info('[AuthTrace] removeUser invoked');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('public_access_token');
      localStorage.removeItem('code_verifier');
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
      state.user = undefined;
      state.token = undefined;
      state.playerLoaded = false;
      // We're fully logged out; stop showing the global auth spinner
      state.requesting = false;
      axios.defaults.headers.common['Authorization'] = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginToSpotify.pending, (state) => {
      state.requesting = true;
    });
    builder.addCase(loginToSpotify.fulfilled, (state, action) => {
      state.token = action.payload.token;
      // Don't set requesting to false here if we're fetching user - let fetchUser handle it
      // Only set to false if we didn't request user fetch (anonymous token)
      if (!action.payload.loaded && !action.payload.redirect) {
        state.requesting = false;
      }
    });
    builder.addCase(loginToSpotify.rejected, (state) => {
      state.requesting = false;
    });
    builder.addCase(fetchUser.pending, (state) => {
      state.requesting = true;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.requesting = false;
    });
    builder.addCase(fetchUser.rejected, (state) => {
      // If fetchUser fails (e.g., 401 after refresh attempt), set requesting to false
      // so CallbackPage can navigate away
      state.requesting = false;
      state.user = undefined;
      // Clear invalid token
      state.token = undefined;
    });
  },
});

export const authActions = { ...authSlice.actions, loginToSpotify, fetchUser };

export default authSlice.reducer;
